import { Component, ElementRef, OnInit, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleCheck, faCircleExclamation, faPencil } from '@fortawesome/free-solid-svg-icons';
import { AuthService } from '../../core/services/auth.service';
import { SessionService } from '../../core/services/session.service';
import { Gateway } from '../../core/interfaces/Gateway';
import { ProvisionGatewayModalComponent } from '../../core/components/provision-gateway-modal/provision-gateway-modal.component';
import { GatewayToolModalComponent, GatewayToolMode } from '../../core/components/gateway-tool-modal/gateway-tool-modal.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';

/** The three columns an admin edits in place on the gateways table. */
type EditableField = 'customerName' | 'notes' | 'activationDate';

@Component({
    selector: 'app-gateways',
    imports: [CommonModule, FormsModule, FontAwesomeModule, NgbDropdownModule, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './gateways.component.html',
    styleUrl: './gateways.component.scss',
})
export class GatewaysComponent implements OnInit {
    gateways: Gateway[] = [];
    loading = false;

    // Font Awesome icons for the Connected column.
    readonly faCircleCheck = faCircleCheck;
    readonly faCircleExclamation = faCircleExclamation;
    /** Beside the description on a customer card, to mark it editable. The
     *  admin table uses a dotted underline instead — a pencil per cell per row
     *  was too repetitive there, but one per card reads fine. */
    readonly faPencil = faPencil;

    newActivationCode = '';
    activating = false;

    /** Admin table search (matches customer / uid / activate key). */
    search = '';

    /** SPY tool — a POST form submits the gateway's `database` here (new tab). */
    readonly spyUrl = 'http://admin.fyes.gr/spy.php';

    constructor(
        private authService: AuthService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) { }

    get isAdmin(): boolean {
        return this.session.isAdmin();
    }

    /** Admin table rows filtered by the search box (customer / uid / code). */
    get filteredGateways(): Gateway[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.gateways;
        return this.gateways.filter((gw) =>
            (gw.customerName || '').toLowerCase().includes(q) ||
            (gw.uid || '').toLowerCase().includes(q) ||
            (gw.code || '').toLowerCase().includes(q) ||
            (gw.notes || '').toLowerCase().includes(q),
        );
    }

    /** What to call a gateway in a confirm dialog — its customer, else its uid. */
    label(gw: Gateway): string {
        return gw.customerName || gw.descr || this.formatUid(gw.uid);
    }

    /** uid currently being saved, so its inputs disable during the request. */
    savingUid: string | null = null;

    /** Which cell is armed for editing, and what it held when editing began.
     *
     *  Blur no longer commits: an admin who clicks away has abandoned the edit,
     *  not confirmed it. Save and Cancel are the only two exits, which is why
     *  the original value has to be kept for Cancel to restore. */
    private editing: { uid: string; field: EditableField } | null = null;
    private editOriginalValue: string | null = null;

    /** uid whose empty date cell has been swapped for a real picker. */
    datePickerUid: string | null = null;

    isEditing(gw: Gateway, field: EditableField): boolean {
        return this.editing?.uid === gw.uid && this.editing?.field === field;
    }

    /** Arm a cell. Any other cell mid-edit is reverted, so at most one is live. */
    beginEdit(gw: Gateway, field: EditableField): void {
        if (this.editing && !this.isEditing(gw, field)) {
            this.revert(this.editing.uid, this.editing.field);
        }
        this.editing = { uid: gw.uid, field };
        this.editOriginalValue = ((gw as any)[field] ?? null) as string | null;
    }

    /** Put a field back to the value it had when editing began. */
    private revert(uid: string, field: EditableField): void {
        const row = this.gateways.find((g) => g.uid === uid);
        if (row) (row as any)[field] = this.editOriginalValue;
    }

    cancelEdit(gw: Gateway, field: EditableField): void {
        if (!this.isEditing(gw, field)) return;
        this.revert(gw.uid, field);
        this.editing = null;
        this.editOriginalValue = null;
    }

    /** Save, but only when the value actually changed. */
    commitEdit(gw: Gateway, field: EditableField): void {
        if (!this.isEditing(gw, field)) return;
        const now = ((gw as any)[field] ?? null) as string | null;
        this.editing = null;
        const before = this.editOriginalValue;
        this.editOriginalValue = null;
        if ((now ?? '') === (before ?? '')) return;
        this.saveMeta(gw, field);
    }

    /** The dash in an empty date cell: swap in the input and open the picker. */
    openDatePicker(gw: Gateway): void {
        this.datePickerUid = gw.uid;
        setTimeout(() => {
            const el = this.dateInput()?.nativeElement;
            el?.focus();
            el?.showPicker?.();
        });
    }

    private readonly dateInput = viewChild<ElementRef<HTMLInputElement>>('dateInput');
    /** Last value saved per field, so a blur with no change is not a request. */
    private lastSaved = new Map<string, string>();

    /** The date input needs yyyy-MM-dd. The date is ours, not the cloud's. */
    dateInputValue(gw: Gateway): string {
        const raw = gw.activationDate;
        if (!raw) return '';
        const d = new Date(raw);
        return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    }

    /**
     * Persists one local field. Called on blur (text) or change (date), and
     * skipped when the value has not actually changed — otherwise tabbing
     * through the table would fire a write per cell.
     */
    saveMeta(gw: Gateway, field: EditableField): void {
        const value = (gw as any)[field] ?? null;
        const key = `${gw.uid}:${field}`;
        if (this.lastSaved.get(key) === String(value ?? '')) return;

        // The activation date doubles as the "already claimed" guard, so the
        // backend refuses to clear it — releasing a gateway is what Tools →
        // Remove customer does. Don't send an empty date at all.
        if (field === 'activationDate' && !value) {
            this.toastr.info('Use Tools → Remove customer to release a gateway.');
            gw.activationDate = this.lastSaved.get(key) || gw.activationDate;
            return;
        }

        this.savingUid = gw.uid;
        // A date input gives yyyy-MM-dd; send it as an ISO instant.
        const payload: any = {};
        payload[field] = field === 'activationDate'
            ? new Date(`${value}T00:00:00.000Z`).toISOString()
            : (value === '' ? null : value);

        this.authService.saveGatewayMeta(gw.uid, payload).subscribe({
            next: (res) => {
                this.savingUid = null;
                this.lastSaved.set(key, String(value ?? ''));
                // Keep the displayed date in step with what was stored.
                if (field === 'activationDate') {
                    gw.activationDate = res?.gateway?.activationDate ?? gw.activationDate;
                }
                gw.hasLocalRecord = true;
                this.toastr.success('Saved');
            },
            error: () => {
                this.savingUid = null;
                this.toastr.error('Unable to save. Reload to see the stored value.');
            },
        });
    }

    /** uid whose description is currently open for editing, if any. */
    editingUid: string | null = null;
    /** Its value when editing began, so Escape can put it back. */
    private editOriginal: string | null = null;

    /** Normalised for comparison: trimmed, and empty means "no description". */
    private descValue(gw: Gateway): string | null {
        return (gw.description ?? '').trim() || null;
    }

    /** The description input, which exists only while a card is being edited. */
    private readonly descInput = viewChild<ElementRef<HTMLInputElement>>("descInput");

    /** Pencil pressed: swap that row's text for an input and focus it. */
    beginEditDescription(gw: Gateway): void {
        this.editingUid = gw.uid;
        this.editOriginal = this.descValue(gw);
        // The input does not exist yet — it appears on the next change
        // detection pass, so the caret has to wait for it.
        setTimeout(() => {
            const el = this.descInput()?.nativeElement;
            el?.focus();
            el?.select();
        });
    }

    /** Escape: put the old value back and close without saving. */
    cancelEditDescription(gw: Gateway): void {
        gw.description = this.editOriginal;
        this.editingUid = null;
    }

    /** Blur or Enter: close the field, and save only on a real change.
     *
     *  The comparison is against the value when editing began, not the
     *  lastSaved map — that map is empty on a fresh page, so opening the pencil
     *  and clicking away without typing would otherwise write to the database
     *  and claim "Saved". */
    commitDescription(gw: Gateway): void {
        if (this.editingUid !== gw.uid) return;
        this.editingUid = null;
        if (this.descValue(gw) === this.editOriginal) return;
        this.saveNickname(gw);
    }

    /**
     * Customer view: persist the description the customer typed. Goes to the
     * session-scoped nickname route, not the admin meta route — that one is
     * behind fyesAdminRequired and would 403 here.
     */
    saveNickname(gw: Gateway): void {
        const value = (gw.description ?? '').trim() || null;
        const key = `${gw.uid}:description`;
        if (this.lastSaved.get(key) === String(value ?? '')) return;

        this.savingUid = gw.uid;
        this.authService.saveGatewayNickname(gw.uid, value).subscribe({
            next: (res) => {
                this.savingUid = null;
                this.lastSaved.set(key, String(value ?? ''));
                // Emptying it falls back to the cloud's own description, so take
                // what the server says rather than assuming the field is blank.
                gw.description = res?.gateway?.nickname ?? gw.descr ?? null;
                this.toastr.success('Saved');
            },
            error: () => {
                this.savingUid = null;
                this.toastr.error('Unable to save. Reload to see the stored value.');
            },
        });
    }

    /**
     * The message a failed request should show. Our API replies with
     * { message, code, subCode }, so prefer its own wording — "Activation code
     * not registered", "This gateway has already been activated" and so on are
     * more use than one catch-all string.
     */
    private errorMessage(err: any, fallback: string): string {
        return err?.error?.message || err?.message || fallback;
    }

    /** Format a dash-stripped uid into 8-char groups for display. */
    formatUid(uid: string): string {
        return uid ? (uid.match(/.{1,8}/g) || [uid]).join('-') : '';
    }

    /** Format an ISO activation date as dd-mm-yyyy (dash if empty/invalid). */
    formatDate(value?: string | null): string {
        if (!value) return '—';
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}-${mm}-${d.getFullYear()}`;
    }

    /** Toggle a gateway's active state (admin). Optimistic; reverts on error. */
    toggleActive(gw: Gateway, event: Event): void {
        const input = event.target as HTMLInputElement;
        const newValue = input.checked;
        this.authService.setGatewayActive(gw.uid, newValue).subscribe({
            next: (res) => {
                console.log('[set-active] backend response:', res);
                gw.active = newValue ? 1 : 0;
            },
            error: (err) => {
                console.error('[set-active] backend error:', err);
                input.checked = !newValue; // revert the UI
            },
        });
    }

    /** Tools → Empty: cloud unbind + clears activation date (re-activatable). */
    empty(gw: Gateway): void {
        if (!confirm(`Empty "${this.label(gw)}"? This unbinds the gateway from its user.`)) return;
        this.authService.emptyGateway(gw.uid).subscribe({
            next: () => {
                this.toastr.success('Gateway released.');
                this.loadGateways();
            },
            // The backend now refuses when the cloud did not actually unbind,
            // and says so — nothing was changed, so the admin needs to know.
            error: (err) => this.toastr.error(
                this.errorMessage(err, 'Unable to empty this gateway. Please try again.')),
        });
    }

    /** Tools → Delete: cloud unbind + retires the code (not re-activatable). */
    deletePermanent(gw: Gateway): void {
        if (!confirm(`Delete "${this.label(gw)}"? This unbinds it and retires its activation code.`)) return;
        this.authService.deleteGateway(gw.uid).subscribe({
            next: () => {
                this.toastr.success('Gateway deleted and its code retired.');
                this.loadGateways();
            },
            error: (err) => this.toastr.error(
                this.errorMessage(err, 'Unable to delete this gateway. Please try again.')),
        });
    }

    /** Tools → Change username / Change password / Replace (modal actions). */
    openTool(mode: GatewayToolMode, gw: Gateway): void {
        const ref = this.modalService.open(GatewayToolModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });
        ref.componentInstance.mode = mode;
        ref.componentInstance.gateway = gw;
        ref.componentInstance.gateways = this.gateways;
        ref.result.then(() => this.loadGateways(), () => { });
    }

    /** True while the code pool is being fetched, so the button can disable. */
    downloadingCodes = false;

    /**
     * Download the unissued activation codes as a text file, one per line and
     * oldest first — the same content the old site's "Download activation
     * codes" button produced.
     *
     * Built here in the browser rather than served as a file: the old handler
     * wrote the pool to a fixed, public path on the web server and left it
     * there. This never touches a disk on our side, and the object URL is
     * revoked as soon as the click is dispatched.
     */
    downloadActivationCodes(): void {
        if (this.downloadingCodes) return;
        this.downloadingCodes = true;

        this.authService.getActivationCodes().subscribe({
            next: (res) => {
                this.downloadingCodes = false;
                const codes: string[] = res?.codes ?? [];
                if (!codes.length) {
                    this.toastr.info('There are no unissued activation codes left.');
                    return;
                }

                // Trailing newline, as the PHP produced ("$code\n" per row).
                const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // Date-stamped, unlike the old fixed activation_code.txt, so
                // successive exports do not collide in the download folder.
                a.download = `activation-codes-${new Date().toISOString().slice(0, 10)}.txt`;
                a.click();
                URL.revokeObjectURL(url);

                this.toastr.success(
                    res?.truncated
                        ? `Downloaded ${codes.length} codes — the pool is larger than the export limit, so this is not all of them.`
                        : `Downloaded ${codes.length} activation ${codes.length === 1 ? 'code' : 'codes'}.`,
                );
            },
            error: (err) => {
                this.downloadingCodes = false;
                this.toastr.error(this.errorMessage(err, 'Unable to download the activation codes.'));
            },
        });
    }

    openProvisionModal(): void {
        const ref = this.modalService.open(ProvisionGatewayModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
        });

        // The gateway is created the moment Save succeeds, not when the modal
        // closes — so the reload has to key off whether it was provisioned, not
        // off how the admin got out. Reloading only on the resolve path meant
        // leaving via the X, Escape or the backdrop left the new gateway
        // missing from the table until a manual refresh.
        const instance = ref.componentInstance as ProvisionGatewayModalComponent;
        const reloadIfCreated = () => {
            if (instance?.provisionedCode) this.loadGateways();
        };
        ref.result.then(reloadIfCreated, reloadIfCreated);
    }

    ngOnInit(): void {
        if (!this.session.isLoggedIn()) {
            // Not logged in — nothing to show here.
            this.router.navigate(['/']);
            return;
        }
        this.loadGateways();
    }

    loadGateways(): void {
        this.loading = true;
        // Admins see every gateway (gw_list with id_user = -1); regular users
        // only their own.
        const request$ = this.isAdmin
            ? this.authService.getAllGateways()
            : this.authService.getGateways();
        request$.subscribe({
            next: (res) => {
                this.gateways = res?.gateways ?? [];
                this.editingUid = null;
                this.editing = null;
                this.editOriginalValue = null;
                this.datePickerUid = null;
                this.loading = false;
            },
            error: () => {
                this.gateways = [];
                this.loading = false;
            },
        });
    }

    /** Redeem an activation code to bind another gateway to this user. */
    activate(): void {
        const code = this.newActivationCode.trim();
        if (!code || this.activating) return;

        this.activating = true;
        this.authService.activateGateway(code).subscribe({
            next: () => {
                this.activating = false;
                this.newActivationCode = '';
                this.toastr.success('Gateway added to your account.');
                this.loadGateways(); // refresh so the new gateway appears
            },
            error: (err) => {
                this.activating = false;
                // The backend distinguishes the cases; show what it says rather
                // than a generic failure. The old site claimed success no matter
                // what happened, which is what we are correcting here.
                this.toastr.error(this.errorMessage(err, 'Unable to add this gateway. Please check the code and try again.'));
            },
        });
    }

}
